import { Translate, useI18n } from '@change-corgi/core-react/i18n';

import { useStarterDashboardV2PageData } from '#src/app/pages/starterDashboardV2/pageContext';
import { PetitionActivityLink } from '#src/app/pages/starterDashboardV2/shared/components';

import { ExportButton } from '../../../shared/ExportButton';
import { useExportButton } from '../../../shared/ExportButton/hooks/useExportButton';
import type { Comment } from '../../types';
import { CommentsList } from '../CommentsList';
import { ExportNotifications } from '../ExportNotifications';

export const CommentsLayout: React.FC<Props> = ({
	setCommentsVisibility,
	comments,
	hasNextPage,
	nextPageLoading,
	loadMore,
}) => {
	const { data, actions } = useExportButton('comments');
	const {
		data: {
			petition: { slug },
			featureConfigs: { starterExperiencePetitionActivity: starterExperiencePetitionActivityFCM },
		},
	} = useStarterDashboardV2PageData();
	const { translate } = useI18n();

	return (
		<section className="bg-background-base flex max-h-[441px] min-h-fit w-full flex-col px-4 pt-8 pb-0 md:p-12 md:pb-0">
			<div
				className="relative flex w-full flex-col overflow-hidden bg-white"
				data-qa="starter-dashboard-comments-card-new"
				data-testid="starter-dashboard-comments-card-new"
			>
				{/* Header Section */}
				<div
					id="petition-comments-card-header"
					className="flex items-center justify-between gap-4 pb-px"
					data-testid="comments-header-new"
				>
					<div data-testid="comments-title-container-new">
						<h3
							className="text-size-heading-small text-typography-strong font-sans"
							id="comments-card-header-new"
							data-qa="comments-card-header-heading-new"
							data-testid="comments-card-header-heading-new"
							aria-label={translate('corgi.petition.starter_dashboard.petition_comments.card.header')}
						>
							<Translate value="corgi.petition.starter_dashboard.petition_comments.card.header" />
						</h3>
					</div>
					<div className="ml-auto justify-self-end" data-testid="comments-export-container-new">
						{starterExperiencePetitionActivityFCM ? (
							<PetitionActivityLink slug={slug} linkType="comments" />
						) : (
							<ExportButton
								data-qa="export-signatures-button-new"
								aria-label="export-comments-button-new"
								aria-describedby="export-comments-button-new"
								exportType="COMMENTS"
								data={data}
								actions={actions}
								data-testid="export-comments-button-new"
							/>
						)}
					</div>
				</div>

				{/* Comments Body */}
				<div data-testid="comments-body-new">
					<CommentsList
						setCommentsVisibility={setCommentsVisibility}
						comments={comments}
						hasNextPage={hasNextPage}
						nextPageLoading={nextPageLoading}
						loadMore={loadMore}
					/>
				</div>
				<ExportNotifications data={data} data-testid="comments-export-notifications-new" />
			</div>
		</section>
	);
};

type Props = Readonly<{
	setCommentsVisibility: (visible: boolean) => void;
	comments: readonly Comment[];
	hasNextPage: boolean;
	nextPageLoading: boolean;
	loadMore: () => void;
}>;
